import type {
  CreateGenerationQueueItemRequest,
  GenerationQueueItemResponse,
  GenerationQueueResponse,
  GenerationQueueStateResponse,
} from '@malaberg/shared';
import { useCallback, useEffect, useRef } from 'react';
import {
  type GenerationQueueItemMutationResponse,
  type GenerationQueueItemsMutationResponse,
  generationQueueApi,
} from '../api/endpoints/generation-queue';
import { useGenerationQueueStore } from '../state/generation-queue-store';
import { useRunStreamStore } from '../state/run-stream-store';

export interface UseGenerationQueueOptions {
  userId?: string;
  autoLoad?: boolean;
}

export interface UseGenerationQueueResult {
  state: GenerationQueueStateResponse | null;
  items: GenerationQueueItemResponse[];
  loading: boolean;
  mutating: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  add: (body: CreateGenerationQueueItemRequest) => Promise<GenerationQueueItemMutationResponse>;
  edit: (
    id: string,
    body: CreateGenerationQueueItemRequest
  ) => Promise<GenerationQueueItemMutationResponse>;
  reorder: (itemIds: string[]) => Promise<GenerationQueueItemsMutationResponse>;
  cancel: (id: string) => Promise<GenerationQueueItemMutationResponse>;
  cancelRemaining: () => Promise<void>;
  requeue: (
    id: string,
    body: CreateGenerationQueueItemRequest
  ) => Promise<GenerationQueueItemMutationResponse>;
  resume: () => Promise<void>;
}

export function useGenerationQueue(
  options: UseGenerationQueueOptions = {}
): UseGenerationQueueResult {
  const { userId, autoLoad = true } = options;
  const state = useGenerationQueueStore((store) => store.state);
  const items = useGenerationQueueStore((store) => store.items);
  const loading = useGenerationQueueStore((store) => store.loading);
  const mutating = useGenerationQueueStore((store) => store.mutating);
  const error = useGenerationQueueStore((store) => store.error);
  const setQueue = useGenerationQueueStore((store) => store.setQueue);
  const setState = useGenerationQueueStore((store) => store.setState);
  const mergeItem = useGenerationQueueStore((store) => store.mergeItem);
  const mergeItems = useGenerationQueueStore((store) => store.mergeItems);
  const setLoading = useGenerationQueueStore((store) => store.setLoading);
  const setMutating = useGenerationQueueStore((store) => store.setMutating);
  const setError = useGenerationQueueStore((store) => store.setError);
  const runsListVersion = useRunStreamStore((store) => store.runsListVersion);

  const generationRef = useRef(0);
  const observedRunsListVersionRef = useRef(runsListVersion);

  const formatError = useCallback((err: unknown) => {
    return err instanceof Error ? err.message : 'Generation queue action failed.';
  }, []);

  const refresh = useCallback(async () => {
    const generation = ++generationRef.current;
    setLoading(true);
    setError(null);

    try {
      const response: GenerationQueueResponse = await generationQueueApi.list({ userId });
      if (generation !== generationRef.current) return;
      setQueue(response.state, response.items);
    } catch (err) {
      if (generation !== generationRef.current) return;
      setError(formatError(err));
    } finally {
      if (generation === generationRef.current) {
        setLoading(false);
      }
    }
  }, [formatError, setError, setLoading, setQueue, userId]);

  const runItemMutation = useCallback(
    async (
      call: () => Promise<GenerationQueueItemMutationResponse>
    ): Promise<GenerationQueueItemMutationResponse> => {
      const generation = ++generationRef.current;
      setLoading(false);
      setMutating(true);
      setError(null);

      try {
        const response = await call();
        if (generation === generationRef.current) {
          mergeItem(response.state, response.item);
        }
        return response;
      } catch (err) {
        if (generation === generationRef.current) {
          setError(formatError(err));
        }
        throw err;
      } finally {
        if (generation === generationRef.current) {
          setMutating(false);
        }
      }
    },
    [formatError, mergeItem, setError, setLoading, setMutating]
  );

  const runItemsMutation = useCallback(
    async (
      call: () => Promise<GenerationQueueItemsMutationResponse>
    ): Promise<GenerationQueueItemsMutationResponse> => {
      const generation = ++generationRef.current;
      setLoading(false);
      setMutating(true);
      setError(null);

      try {
        const response = await call();
        if (generation === generationRef.current) {
          mergeItems(response.state, response.items);
        }
        return response;
      } catch (err) {
        if (generation === generationRef.current) {
          setError(formatError(err));
        }
        throw err;
      } finally {
        if (generation === generationRef.current) {
          setMutating(false);
        }
      }
    },
    [formatError, mergeItems, setError, setLoading, setMutating]
  );

  useEffect(() => {
    if (!autoLoad) return;
    void refresh();
  }, [autoLoad, refresh]);

  useEffect(() => {
    if (runsListVersion === observedRunsListVersionRef.current) return;
    observedRunsListVersionRef.current = runsListVersion;
    if (runsListVersion === 0) return;

    void refresh();
  }, [refresh, runsListVersion]);

  const add = useCallback(
    (body: CreateGenerationQueueItemRequest) => {
      return runItemMutation(() => generationQueueApi.add(body));
    },
    [runItemMutation]
  );

  const edit = useCallback(
    (id: string, body: CreateGenerationQueueItemRequest) => {
      return runItemMutation(() => generationQueueApi.edit(id, body));
    },
    [runItemMutation]
  );

  const reorder = useCallback(
    (itemIds: string[]) => {
      return runItemsMutation(() => generationQueueApi.reorder({ itemIds }));
    },
    [runItemsMutation]
  );

  const cancel = useCallback(
    (id: string) => {
      return runItemMutation(() => generationQueueApi.cancel(id));
    },
    [runItemMutation]
  );

  const cancelRemaining = useCallback(async () => {
    const generation = ++generationRef.current;
    setLoading(false);
    setMutating(true);
    setError(null);

    try {
      await generationQueueApi.cancelRemaining();
      if (generation === generationRef.current) {
        await refresh();
      }
    } catch (err) {
      if (generation === generationRef.current) {
        setError(formatError(err));
      }
      throw err;
    } finally {
      setMutating(false);
    }
  }, [formatError, refresh, setError, setLoading, setMutating]);

  const requeue = useCallback(
    (id: string, body: CreateGenerationQueueItemRequest) => {
      return runItemMutation(() => generationQueueApi.requeue(id, body));
    },
    [runItemMutation]
  );

  const resume = useCallback(async () => {
    const generation = ++generationRef.current;
    setLoading(false);
    setMutating(true);
    setError(null);

    try {
      const response = await generationQueueApi.resume();
      if (generation === generationRef.current) {
        setState(response.state);
      }
    } catch (err) {
      if (generation === generationRef.current) {
        setError(formatError(err));
      }
      throw err;
    } finally {
      if (generation === generationRef.current) {
        setMutating(false);
      }
    }
  }, [formatError, setError, setLoading, setMutating, setState]);

  return {
    state,
    items,
    loading,
    mutating,
    error,
    refresh,
    add,
    edit,
    reorder,
    cancel,
    cancelRemaining,
    requeue,
    resume,
  };
}
